import _ from 'lodash';
import {
    ProviderInstitutionalClaimTemplate,
    ProviderNynf3ClaimTemplate,
    ProviderProfessionalClaimTemplate,
} from '../../../api';
import { formatNumber, parseAmount } from '../../../services/primitives/number-format';
import { ITimelineEvent, ITimelineEventKind } from './claim-timeline.types';
import { hasGenericTimelineEventDetail } from './timeline-event-detail';

const SYSTEM_DETAIL_KINDS: ITimelineEventKind[] = ['transmission', 'eligibility', 'coding', 'workerfunction', 'claimdata'];

export function isMessageTimelineEvent(item: ITimelineEvent): boolean {
    return (item.event || item.type) === 'Message';
}

export function canTimelineRowToggleExpansion(item: ITimelineEvent): boolean {
    return (
        !!item.details ||
        hasGenericTimelineEventDetail(item) ||
        (isMessageTimelineEvent(item) && !!item.onClick)
    );
}

export function isTimelineEventExpandable(item: ITimelineEvent): boolean {
    return canTimelineRowToggleExpansion(item);
}

export function isSystemTimelineDetailKind(kind?: ITimelineEventKind): boolean {
    return !!kind && SYSTEM_DETAIL_KINDS.includes(kind);
}

export function isClaimDataTimelineEvent(item: ITimelineEvent): boolean {
    if (item.kind === 'claimdata') {
        return true;
    }

    const label = item.event || item.type || '';
    return label === 'Claim Data' || label === 'Provider Claim Update';
}

export function getTimelineEventLabel(item: ITimelineEvent): string {
    if (isClaimDataTimelineEvent(item) && item.claimDataNum != null) {
        return `Claim Data (${item.claimDataNum})`;
    }

    return item.event || item.type || '';
}

/**
 * Canonical filter/group key for an event. Claim data variants collapse to "Claim Data".
 */
export function getTimelineFilterKey(item: ITimelineEvent): string | undefined {
    if (isClaimDataTimelineEvent(item)) {
        return 'Claim Data';
    }

    return item.event || item.type;
}

/**
 * Numbers claim data events chronologically (oldest = 1) for display labels.
 * Timeline items are sorted newest-first; numbering walks oldest to newest.
 */
export function assignClaimDataNumbers(items: ITimelineEvent[]): ITimelineEvent[] {
    const numbered = items.map(item => ({ ...item }));
    let seen = 0;

    for (let i = numbered.length - 1; i >= 0; i -= 1) {
        if (!isClaimDataTimelineEvent(numbered[i])) {
            continue;
        }

        seen += 1;
        if (numbered[i].claimDataNum == null) {
            numbered[i].claimDataNum = seen;
        }
    }

    return numbered;
}

export function getTimelineGridColumns(
    appType: 'provider' | 'support',
    options: { showTags?: boolean; showActions?: boolean },
): string {
    const base = '200px minmax(220px, 1fr) 1.3fr';
    const typeColumn = appType === 'provider' ? ' 150px' : '';
    const tagsColumn = appType === 'support' && options.showTags ? ' minmax(120px, 200px)' : '';
    const actionsColumn = appType === 'support' && options.showActions ? ' minmax(80px, 100px)' : '';

    return `${base}${typeColumn}${tagsColumn}${actionsColumn} 28px`;
}

export type ClaimTemplateForServiceLines =
    | ProviderProfessionalClaimTemplate
    | ProviderInstitutionalClaimTemplate
    | ProviderNynf3ClaimTemplate;

export interface IClaimTemplateServiceLinesSummary {
    count: number;
    total: number;
}

export function getClaimTemplateServiceLinesSummary(
    template: ClaimTemplateForServiceLines | undefined,
): IClaimTemplateServiceLinesSummary | undefined {
    if (!template) {
        return undefined;
    }

    let lines: Array<{ amount?: { amount?: string } }> = [];

    if ('f24ServiceLine' in template && template.f24ServiceLine?.length) {
        lines = template.f24ServiceLine.map(line => ({ amount: line.f24fChargeAmount }));
    } else if ('f42ClaimLines' in template && template.f42ClaimLines?.length) {
        lines = template.f42ClaimLines.map(line => ({ amount: line.f47TotalChargeAmount }));
    } else if ('f15ServiceLines' in template && template.f15ServiceLines?.length) {
        lines = template.f15ServiceLines.map(line => ({ amount: line.f15eChargeAmount }));
    }

    if (!lines.length) {
        return undefined;
    }

    return {
        count: lines.length,
        total: _.sumBy(lines, line => parseAmount(line.amount)),
    };
}

/**
 * Claim Created facts card label, e.g. "2 lines · $687.88".
 */
export function formatClaimTemplateServiceLines(
    template: ClaimTemplateForServiceLines | undefined,
): string | undefined {
    const summary = getClaimTemplateServiceLinesSummary(template);
    if (!summary) {
        return undefined;
    }

    const lineLabel = summary.count === 1 ? '1 line' : `${summary.count} lines`;
    const amountLabel = formatNumber('Currency')(summary.total);

    return `${lineLabel} · ${amountLabel}`;
}

